/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Wizard selectors
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.4.0
 * @since 1.2.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { IRootReducer } from '../../reducers/rootReducer';
import { EBasketType } from '../app/appConstants';
import { EWizardName } from './wizardConstants';
import { IWizardState } from './wizardReducer';
import { TWizardStepNames } from './wizardTypes';

export const getWizardState = (state: IRootReducer) => state.wizard;

export const makeSelectWizardName = ():
    OutputSelector<IRootReducer, EWizardName, (state: IWizardState) => EWizardName> =>
    createSelector(
        getWizardState,
        state => state.name,
    );

export const makeSelectWizardBasketType = ():
    OutputSelector<IRootReducer, EBasketType, (state: IWizardState) => EBasketType> =>
    createSelector(
        getWizardState,
        state => state.basketType,
    );

export const makeSelectWizardStepName = ():
    OutputSelector<IRootReducer, TWizardStepNames, (state: IWizardState) => TWizardStepNames> =>
    createSelector(
        getWizardState,
        state => state.stepName,
    );

export const makeSelectWizardSteps = ():
    OutputSelector<IRootReducer, string[], (state: IWizardState) => string[]> =>
    createSelector(
        getWizardState,
        state => state.steps,
    );

export const makeSelectWizardIsFinal = ():
    OutputSelector<IRootReducer, boolean, (state: IWizardState) => boolean> =>
    createSelector(
        getWizardState,
        state => state.isFinal,
    );

export const makeSelectWizardIsShowLoader = ():
    OutputSelector<IRootReducer, boolean, (state: IWizardState) => boolean> =>
    createSelector(
        getWizardState,
        state => state.isShowLoader,
    );

export const makeSelectWizardIsStart = ():
    OutputSelector<IRootReducer, boolean, (state: IWizardState) => boolean> =>
    createSelector(
        getWizardState,
        state => state.isStart,
    );

export const makeSelectWizardBasketSnapshotHeader = ():
    OutputSelector<IRootReducer, string, (state: IWizardState) => string> =>
    createSelector(
        getWizardState,
        state => `${state.basketSnapshotType}:${state.basketSnapshotId}`,
    );

export const makeSelectWizardBasketSnapshotId = ():
    OutputSelector<IRootReducer, string, (state: IWizardState) => string> =>
    createSelector(
        getWizardState,
        state => state.basketSnapshotId,
    );

export const makeSelectWizardBasketSnapshotType = ():
    OutputSelector<IRootReducer, string, (state: IWizardState) => string> =>
    createSelector(
        getWizardState,
        state => state.basketSnapshotType,
    );

export const makeSelectWizardCurrentAccount = ():
    OutputSelector<IRootReducer, number, (state: IWizardState) => number> =>
    createSelector(
        getWizardState,
        state => state.entityId,
    );

export const makeSelectWizardIsRequestsSticky = ():
    OutputSelector<IRootReducer, boolean, (state: IWizardState) => boolean> =>
    createSelector(
        getWizardState,
        state => state.isRequestsSticky,
    );
